import { useState, useMemo, useCallback } from "react";
import { useDebounce } from "../../../../../hooks/useDebounce";
import LimitSelect from "../../../../../components/elements/LimitSelect";
import Search from "../../../../../components/form/Search";
import Table from "../../../../../components/elements/table/Table";
import dayjs from "dayjs";
import { lngDetect } from "../../../../../helpers/lngDetect";
import { handleFilterChange } from "../../../../../helpers";
import { tableFilter } from "../../../../../helpers/tableFilterOptions";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useOutletContext } from "react-router-dom";
import { useOrganization } from "../../../../../context/OrganizationContext";
import * as XLSX from "xlsx";
import { saleLReportist } from "../../../../../services/eventReportService";
import ExportDropdown from "../ExportDropdown";
import Button from "../../../../form/Button";
import SaleDrawer from "./SaleDrawer";
import {
  reportCountList,
  reportExport,
} from "../../../../../services/reportService";
import {
  createCSVContent,
  createExcelFile,
  downloadFile,
} from "../CommonExport";
import Skeleton from "../../../../loader/Skeleton";
import Badge from "../../../../elements/Badge";
import { t } from "i18next";

// Constants moved outside component to prevent recreation
const PRODUCT_TYPE_LIST = [
  { name: "Ticket", value: "ticket", type: "checkbox" },
  { name: "Addon", value: "addons-event", type: "checkbox" },
];

const PAYMENT_TYPE_LIST = [
  { name: "Free", value: "free", type: "checkbox" },
  { name: "Paid", value: "paid", type: "checkbox" },
  { name: "Donation", value: "donation", type: "checkbox" },
  { name: "Combo", value: "combo", type: "checkbox" },
];

const SALE_STATUS_STYLES = {
  complete: {
    backgroundColor: "#D1FAE5",
    color: "#047857",
    borderColor: "#10B981",
  },
  due: {
    backgroundColor: "#FEE2E2",
    color: "#DC2626",
    borderColor: "#EF4444",
  },
};

const PRODUCT_TYPE_STYLES = {
  ticket: {
    backgroundColor: "#EDE9FE",
    color: "#7C3AED",
    borderColor: "#8B5CF6",
  },
  "add-on": {
    backgroundColor: "#ECFDF5",
    color: "#059669",
    borderColor: "#10B981",
  },
};

const PAYMENT_TYPE_STYLES = {
  free: {
    backgroundColor: "#EDE9FE",
    color: "#7C3AED",
    borderColor: "#8B5CF6",
  },
  donation: {
    backgroundColor: "#ECFDF5",
    color: "#059669",
    borderColor: "#10B981",
  },
  paid: {
    backgroundColor: "#D1FAE5",
    color: "#047857",
    borderColor: "#10B981",
  },
  combo: {
    backgroundColor: "#DBEAFE",
    color: "#1D4ED8",
    borderColor: "#3B82F6",
  },
};

const DEFAULT_STYLE = {
  backgroundColor: "#F3F4F6",
  color: "#374151",
  borderColor: "#6B7280",
};

const getSaleStatusStyle = (status) => {
  return SALE_STATUS_STYLES[status?.toLowerCase()] || DEFAULT_STYLE;
};

const getProductTypeStyle = (type) => {
  return PRODUCT_TYPE_STYLES[type?.toLowerCase()] || DEFAULT_STYLE;
};

const getPaymentTypeStyle = (type) => {
  return PAYMENT_TYPE_STYLES[type?.toLowerCase()] || DEFAULT_STYLE;
};

const SaleList = () => {
  const { event } = useOutletContext();
  const { selectedOrganization } = useOrganization();
  const [selectedItems, setSelectedItems] = useState([]);
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });
  const [drawerOpen, setDrawerOpen] = useState({ isOpen: false, data: null });
  const [additionalFilters, setAdditionalFilters] = useState();
  const [eventCountFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  const {
    data: eventCount,
    isLoading: eventCountLoading,
    refetch: eventRefetch,
  } = useQuery({
    queryKey: ["eventCountData", eventCountFilters, event?.data?._id],
    queryFn: async () => {
      const res = await reportCountList({
        ...eventCountFilters,
        ...additionalFilters,
        event: [event?.data?._id],
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.data;
    },
    enabled: !!event?.data?._id,
  });

  // Memoized query key
  const queryKey = useMemo(
    () => ["sale", filters, search, selectedOrganization, event?.data?._id],
    [filters, search, selectedOrganization, event?.data?._id]
  );

  // Fetch sales data
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await saleLReportist({
        ...filters,
        ...additionalFilters,
        ...(search && { keyword: search }),
        organization: selectedOrganization,
        event: event?.data?._id,
        orderStatus: ["confirmed"],
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!event?.data?._id,
  });

  // Send file to API
  const sendFileToAPI = useCallback(
    async ({ fileContent, localDownloadStatus, fileName, format, event }) => {
      try {
        const formData = new FormData();
        formData.append("event", event);
        formData.append("file", fileContent, fileName);
        formData.append("type", "sales-summary");
        formData.append("name", `Sales Report (${format?.toUpperCase()})`);
        formData.append("organization", selectedOrganization);
        formData.append("status", localDownloadStatus ? "success" : "failed");

        const res = await reportExport(formData);
        return res?.status === 200 || res?.status === 201;
      } catch (error) {
        toast.warning(
          "File downloaded successfully, but failed to send to server"
        );
        return false;
      }
    },
    [selectedOrganization]
  );

  // Unified export handler
  const handleExport = useCallback(
    async (format) => {
      setIsExportOpen(false);
      toast.info("Preparing export data...");

      try {
        const exportResponse = await saleLReportist({
          ...filters,
          ...(search && { keyword: search }),
          limit: data?.metadata?.totalDocs,
          page: 1,
          organization: selectedOrganization,
          event: event?.data?._id,
          orderStatus: ["confirmed"],
        });

        if (exportResponse?.docs?.status !== 200) {
          toast.error(exportResponse?.message || "Failed to fetch export data");
          return;
        }

        const saleData = exportResponse?.docs?.data || [];
        if (!saleData || saleData.length === 0) {
          toast.warning("No data available to export");
          return;
        }

        // Transform data
        const exportData = saleData.map((sale) => {
          const attendee_name = sale?.attendeeData
            ?.filter((attendee) =>
              ["First Name", "Last Name"].includes(attendee?.question)
            )
            ?.map((attendee) => `${attendee?.answers || ""}`.trim())
            .join(" ");
          const attendee_email = sale?.attendeeData
            ?.filter((attendee) => ["Email"].includes(attendee?.question))
            ?.map((attendee) => `${attendee?.answers || ""}`.trim())
            .join(" ");
          return {
            "Ticket Name": lngDetect(sale?.ticketInfo?.name) || "-",
            "Product Type":
              sale?.ticketInfo?.type
                ?.split("-")
                ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                ?.join(" ") || "-",
            "Booking / Full paid / free ticket":
              sale?.ticketInfo?.paymentType !== "free"
                ? sale?.ticketInfo?.fullPaid === "no" &&
                  sale?.fullPaid !== "yes"
                  ? "Booking"
                  : "full paid"
                : "Free",
            // sale?.ticketInfo?.paymentType
            //   .split("-")
            //   .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            //   .join(" "),
            // Price: "$" + (sale?.ticketInfo?.fullPrice || 0).toString(),
            "Booking Amount":
              sale?.ticketInfo?.paymentType === "paid"
                ? "$" + (sale?.totalPrice || 0).toString()
                : "$" + 0,
            "Total Payment":
              sale?.ticketInfo?.paymentType === "paid"
                ? "$" + (sale?.finalPrice || 0).toString()
                : "$" + 0,
            // "Payment Status":
            //   sale?.ticketInfo?.paymentType === "free"
            //     ? "Complete"
            //     : sale?.fullPaid === "yes"
            //     ? "Complete"
            //     : "Due",
            "Order Date": sale.createdAt
              ? dayjs(sale.createdAt).format("MM/DD/YYYY")
              : "-",
            Attendee: attendee_name || "-",
          };
        });

        const filename = `sales_export_${dayjs().format("YYYY-MM-DD")}`;

        let blob, fullFilename;
        if (format === "csv") {
          const csvContent = createCSVContent(exportData);
          blob = new Blob([csvContent], { type: "text/csv" });
          fullFilename = `${filename}.csv`;
        } else {
          const workbook = createExcelFile(exportData);
          if (format === "xlsx") {
            const excelBuffer = XLSX.write(workbook, {
              bookType: "xlsx",
              type: "array",
            });
            blob = new Blob([excelBuffer], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            fullFilename = `${filename}.xlsx`;
          }
        }

        const downloadSuccess = await downloadFile(blob, fullFilename);

        if (downloadSuccess) {
          toast.success(
            `${format.toUpperCase()} file ${
              window.showSaveFilePicker ? "saved" : "downloaded"
            } successfully!`
          );

          try {
            await sendFileToAPI({
              fileName: fullFilename,
              fileContent: blob,
              localDownloadStatus: downloadSuccess,
              format: format,
              event: event?.data?._id,
            });
          } catch (apiError) {
            console.error("API upload failed:", apiError);
            if (window.showSaveFilePicker) {
              toast.warning("File saved locally but failed to sync to server");
            }
          }
        }
      } catch (error) {
        toast.error("Export failed. Please try again.");
      }
    },
    [
      filters,
      search,
      data?.metadata?.totalDocs,
      selectedOrganization,
      event?.data?._id,
      createCSVContent,
      createExcelFile,
      downloadFile,
      sendFileToAPI,
    ]
  );

  // Memoized table head data
  const tableHeadData = [
    {
      _id: 1,
      name: t("Ticket Name"),
      align: "left",
    },
    {
      _id: 2,
      name: t("Ticket Type"),
      align: "left",
      ...tableFilter({
        filterKey: "ticketType",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val, page: 1 }));
        },
        filterList: [...PRODUCT_TYPE_LIST],
      }),
    },
    {
      _id: 3,
      name: t("Booking / Full paid / free ticket"),
      align: "left",
      // ...tableFilter({
      //   filterKey: "paymentType",
      //   filters,
      //   setFilters: (val) => {
      //     setFilters((pre) => ({ ...pre, ...val, page: 1 }));
      //   },
      //   filterList: [...PAYMENT_TYPE_LIST],
      // }),
    },
    // {
    //   _id: 4,
    //   name: "Price",
    //   align: "left",
    // },
    {
      _id: 5,
      name: t("Booking Payment"),
      align: "left",
    },
    {
      _id: 6,
      name: t("Total Payment"),
      align: "left",
    },
    {
      _id: 9,
      name: t("Order Date"),
      align: "left",
    },
    // {
    //   _id: 7,
    //   name: "Payment Status",
    //   align: "left",
    // },
    {
      _id: 8,
      name: t("Attendee"),
      align: "left",
    },
  ];

  // Memoized table data with optimized rendering
  const tableData = useMemo(() => {
    return (
      data?.data?.map((item) => {
        // const paymentStatus =
        //   item?.ticketInfo?.paymentType === "free"
        //     ? "Complete"
        //     : item?.fullPaid === "yes"
        //     ? "Complete"
        //     : "Due";
        // const paymentStyle = getSaleStatusStyle(paymentStatus);

        const productType = item?.ticketInfo?.type || "ticket";
        const productTypeText = productType
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
        const productTypeStyle = getProductTypeStyle(productType);

        // const paymentType = item?.ticketInfo?.paymentType || "ticket";
        // const paymentTypeText = paymentType
        //   .split("-")
        //   .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        //   .join(" ");
        // const paymentTypeStyle = getPaymentTypeStyle(paymentType);

        const attendee_name = item?.attendeeData
          ?.filter((attendee) =>
            ["First Name", "Last Name"].includes(attendee?.question)
          )
          ?.map((attendee) => `${attendee?.answers || ""}`.trim())
          .join(" ");
        const attendee_email = item?.attendeeData
          ?.filter((attendee) => ["Email"].includes(attendee?.question))
          ?.map((attendee) => `${attendee?.answers || ""}`.trim())
          .join(" ");

        return {
          _id: item?._id,
          rowData: [
            {
              _id: 2,
              type: "text",
              align: "left",
              cellClass: "!min-w-[150px] font-medium",
              data: lngDetect(item?.ticketInfo?.name) || "-",
            },
            {
              _id: 3,
              type: "text",
              align: "left",
              cellClass: "!min-w-[100px]",
              data: (
                <span
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap"
                  style={productTypeStyle}
                >
                  {productTypeText}
                </span>
              ),
            },
            {
              _id: 4,
              type: "text",
              align: "left",
              cellClass: "!min-w-[100px]",
              data:
                item?.ticketInfo?.paymentType !== "free" ? (
                  <Badge
                    type={
                      item?.ticketInfo?.fullPaid !== "no" ||
                      item?.fullPaid === "yes"
                        ? "danger"
                        : "warning"
                    }
                    className={"rounded-full py-1 text-xs font-medium"}
                    label={
                      item?.ticketInfo?.fullPaid === "no" &&
                      item?.fullPaid !== "yes"
                        ? "booking"
                        : "full paid"
                    }
                  />
                ) : (
                  <Badge
                    type={"info"}
                    className={"rounded-full py-1 text-xs font-medium"}
                    label={"free"}
                  />
                ),
              // (
              //   <span
              //     className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border"
              //     style={paymentTypeStyle}
              //   >
              //     {paymentTypeText}
              //   </span>
              // ),
            },
            // {
            //   _id: 6,
            //   type: "text",
            //   align: "left",
            //   cellClass: "!min-w-[100px] font-medium",
            //   data: "$" + (item?.ticketInfo?.fullPrice || 0).toString(),
            // },
            {
              _id: 7,
              type: "text",
              align: "left",
              cellClass: "!min-w-[100px] font-medium",
              data:
                item?.ticketInfo?.paymentType === "paid"
                  ? `${item?.eventData?.currency?.symbol || "$"} ${(
                      item?.totalPrice || 0
                    ).toFixed(2)}`
                  : "-",
            },
            {
              _id: 8,
              type: "text",
              align: "left",
              cellClass: "!min-w-[120px] font-semibold text-green-600",
              data:
                item?.ticketInfo?.paymentType === "paid"
                  ? `${item?.eventData?.currency?.symbol || "$"} ${(
                      item?.finalPrice || 0
                    ).toFixed(2)}`
                  : "-",
            },
            // {
            //   _id: 9,
            //   type: "text",
            //   align: "left",
            //   cellClass: "!min-w-[120px] font-semibold text-green-600",
            //   data: (
            //     <span
            //       className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap"
            //       style={paymentStyle}
            //     >
            //       {paymentStatus}
            //     </span>
            //   ),
            // },
            {
              _id: 11,
              type: "text",
              align: "left",
              cellClass: "!min-w-[140px] text-gray-600",
              data:
                dayjs(item?.createdAt).format("MMM DD, YYYY hh:mm A") || "-",
            },
            {
              _id: 10,
              type: "text",
              align: "left",
              cellClass: "!min-w-[150px] text-purple-600",
              data: (
                <div>
                  <div className="flex items-center gap-1">
                    <span>{attendee_name || "-"}</span>
                  </div>
                  <div className="text-xs text-gray-400 flex items-center gap-1">
                    <i className="fa-fw fa-regular fa-envelope"></i>
                    {attendee_email || item?.userData?.email}
                  </div>
                </div>
              ),
            },
          ],
        };
      }) || []
    );
  }, [data?.data]);

  // Memoized callbacks
  const handleRunReport = useCallback(async () => {
    try {
      setDrawerOpen({ isOpen: false, data: null });
      await refetch();
      await eventRefetch();
    } catch (error) {
      toast.error("Failed to generate report. Please try again.");
    }
  }, [refetch, eventRefetch]);

  const handleLimitChange = useCallback((val) => {
    handleFilterChange("limit", val, setFilters);
  }, []);

  const handleSearch = useCallback((val) => {
    setKeyword(val);
    handleFilterChange("page", 1, setFilters);
  }, []);

  const handlePageChange = useCallback((val) => {
    handleFilterChange("page", val, setFilters);
    setSelectedItems([]);
  }, []);

  const toggleExportDropdown = useCallback(() => {
    setIsExportOpen(!isExportOpen);
  }, [isExportOpen]);

  const openFilterDrawer = useCallback(() => {
    setDrawerOpen((prev) => ({ ...prev, isOpen: true }));
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen({ isOpen: false, data: null });
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <Button
            buttonLabel={t("Filters")}
            buttonIcon={"fas fa-filter text-sm"}
            buttonFunction={openFilterDrawer}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-gray-600 text-sm font-medium mb-2">
            {t("Net Sales")}
          </h3>
          <div className="text-2xl md:text-3xl font-bold text-gray-900">
            {eventCountLoading ? (
              <Skeleton width={120} height={36} />
            ) : eventCount?.netSales ? (
              `${
                event?.data?.currency?.symbol || "$"
              } ${eventCount?.netSales?.toFixed(2)}`
            ) : (
              0
            )}
          </div>
        </div>

        {/* Tickets + Add-Ons Sold */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-gray-600 text-sm font-medium mb-2">
            {t("Tickets + Add-Ons Sold")}
          </h3>
          <div className="text-2xl md:text-3xl font-bold text-gray-900">
            {eventCountLoading ? (
              <Skeleton width={60} height={36} />
            ) : eventCount ? (
              eventCount?.ticketsSold + eventCount?.addonsSold
            ) : (
              0
            )}
          </div>
        </div>

        {/* Orders */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-gray-600 text-sm font-medium mb-2">
            {t("Orders")}
          </h3>
          <div className="text-2xl md:text-3xl font-bold text-gray-900">
            {eventCountLoading ? (
              <Skeleton width={30} height={36} />
            ) : (
              eventCount?.totalOrder
            )}
          </div>
        </div>
      </div>

      {/* Search and Controls */}
      <div className="flex flex-wrap gap-3 md:gap-0 items-center justify-between">
        <div className="flex flex-wrap items-center gap-1 md:gap-3">
          <div className="w-full md:w-36">
            <LimitSelect
              value={filters?.limit}
              allValue={data?.metadata?.totalDocs}
              onChange={handleLimitChange}
            />
          </div>
          <div className="w-full md:w-72 mt-3">
            <Search placeholder={t("Search here")} search={handleSearch} />
          </div>
        </div>
        <div className="flex gap-4 items-center">
          <ExportDropdown
            onExport={handleExport}
            isOpen={isExportOpen}
            onToggle={toggleExportDropdown}
            isLoading={isExporting}
          />
        </div>
      </div>

      {/* Sales Table */}
      <Table
        tableHeadData={tableHeadData}
        tableData={tableData}
        isLoder={isLoading}
        pageCount={data?.metadata?.totalPages}
        currentPage={filters?.page}
        onPageChange={handlePageChange}
        originalData={[...(data?.data || [])]}
        setOriginalData={(val) => {
          queryClient.setQueryData(queryKey, (state) => {
            if (state) {
              return { ...state, data: val };
            }
          });
        }}
        isFetching={isFetching}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        tableActionDropdownContainer={"!w-48"}
      />

      <SaleDrawer
        open={drawerOpen.isOpen}
        close={closeDrawer}
        filters={additionalFilters}
        setFilters={setAdditionalFilters}
        onRunReport={handleRunReport}
      />
    </div>
  );
};

export default SaleList;
